import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PlusIcon, ChartBarIcon, ClockIcon, PencilIcon, MagnifyingGlassIcon, TrashIcon } from '@heroicons/react/24/outline';
import Header from '../components/layout/Header';
import Button from '../components/common/Button';
import Toast, { ToastType } from '../components/common/Toast';
import RestaurantForm from '../components/restaurants/RestaurantForm';
import Modal from '../components/common/Modal';
import { useEmployees } from '../hooks/useEmployees';
import kpiService from '../services/kpiService';
import timesheetService from '../services/timesheetService';
import { TimesheetEntry } from '../types/timesheet';
import { useToast } from '../hooks/useToast';
import { useUser } from '../hooks/useUser';
import { useAdmin } from '../hooks/useAdmin';
import { useRestaurants } from '../hooks/useRestaurants';
import { highlightMatch } from '../utils/highlightText';
import { formatMoney } from '../utils/timeUtils';

interface KPIEntry {
  kpi_id: string;
  employee_name: string;
  position: string;
  phone: string;
  manual_amount: number;
  note: string;
  sheet_row?: number;
  synced?: boolean;
}

const KPIPage: React.FC = () => {
  // Получаем telegram_id для сохранения выбора ресторана в БД
  const { telegramId } = useUser();
  
  // Проверяем права администратора
  const { isAdmin } = useAdmin(telegramId);
  
  // Используем хук для управления ресторанами (БД + localStorage)
  const { 
    restaurants, 
    selectedRestaurantId, 
    setSelectedRestaurantId,
    fetchRestaurantSuggestions: fetchRestaurantsHook
  } = useRestaurants({
    telegramId,
    autoLoadLastRestaurant: true
  });
  
  const { toasts, hideToast, success, error: showError } = useToast();
  
  const [isRestaurantFormOpen, setIsRestaurantFormOpen] = useState(false);
  const [kpiEntries, setKpiEntries] = useState<KPIEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeView, setActiveView] = useState<'menu' | 'kpi' | 'shifts'>('menu');
  const [updatingKpiId, setUpdatingKpiId] = useState<string | null>(null);
  const [lastLoadedDate, setLastLoadedDate] = useState<string | null>(null);
  
  // Для модального окна KPI
  const [isKpiModalOpen, setIsKpiModalOpen] = useState(false);
  const [editingKpiId, setEditingKpiId] = useState<string | null>(null);
  const [kpiFormData, setKpiFormData] = useState({ employee_name: '', manual_amount: '', note: '' });
  const [showEmployeeSuggestions, setShowEmployeeSuggestions] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState<Array<{name: string, position: string}>>([]);
  const employeeInputRef = useRef<HTMLDivElement>(null);
  
  // Для автодополнения сотрудников в KPI
  const { suggestions: employeeSuggestions } = useEmployees({ 
    restaurantId: selectedRestaurantId || undefined 
  });
  
  
  // Для работы с табелем часов
  const [timesheetEntries, setTimesheetEntries] = useState<TimesheetEntry[]>([]);
  const [timesheetLoading, setTimesheetLoading] = useState(false);
  const [timesheetSaving, setTimesheetSaving] = useState(false);
  const [editingTimesheetRow, setEditingTimesheetRow] = useState<number | null>(null);
  const [timesheetEditData, setTimesheetEditData] = useState({ name: '', hours: '', start_time: '', end_time: '', manual_amount: '' });
  const [isAddTimesheetOpen, setIsAddTimesheetOpen] = useState(false);
  const [newTimesheetEntry, setNewTimesheetEntry] = useState({ name: '', hours: '', manual_amount: '' });
  const [showTimesheetSuggestions, setShowTimesheetSuggestions] = useState(false);
  const [filteredTimesheetEmployees, setFilteredTimesheetEmployees] = useState<Array<{name: string, position: string}>>([]);
  const timesheetInputRef = useRef<HTMLDivElement>(null);
  const [timesheetSearchQuery, setTimesheetSearchQuery] = useState('');
  
  
  // Фильтрация сотрудников для KPI
  useEffect(() => {
    if (showEmployeeSuggestions && !editingKpiId) {
      if (kpiFormData.employee_name.trim()) {
        // Фильтруем если что-то введено (ищем по имени И должности)
        const filtered = employeeSuggestions.filter((emp: any) =>
          emp.name.toLowerCase().includes(kpiFormData.employee_name.toLowerCase()) ||
          emp.position.toLowerCase().includes(kpiFormData.employee_name.toLowerCase())
        );
        setFilteredEmployees(filtered);
      } else {
        // Показываем все имена если поле пустое
        setFilteredEmployees(employeeSuggestions);
      }
    }
  }, [kpiFormData.employee_name, employeeSuggestions, editingKpiId, showEmployeeSuggestions]);
  
  // Закрытие suggestions при клике вне
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (employeeInputRef.current && !employeeInputRef.current.contains(event.target as Node)) {
        setShowEmployeeSuggestions(false);
      }
      if (timesheetInputRef.current && !timesheetInputRef.current.contains(event.target as Node)) {
        setShowTimesheetSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Дата "вчера"
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toLocaleDateString('ru-RU', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  // Загрузка начисленных KPI за вчера
  const fetchKPIEntries = useCallback(async () => {
    if (!selectedRestaurantId) return;
    
    setLoading(true);
    try {
      const yesterdayDate = new Date();
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);
      const dateStr = yesterdayDate.toISOString().split('T')[0];
      
      // Проверяем, нужно ли обновить данные (сменился ли день)
      if (lastLoadedDate && lastLoadedDate !== dateStr) {
        console.log(`День сменился: ${lastLoadedDate} -> ${dateStr}. Обновляем KPI.`);
      }
      
      const response = await kpiService.getEmployeeKPIs({ 
        restaurant_id: selectedRestaurantId,
        date_from: dateStr,
        date_to: dateStr,
      });
      
      setKpiEntries(response.kpis.map((kpi: any) => ({
        kpi_id: kpi.kpi_id,
        employee_name: kpi.employee_name,
        position: kpi.position,
        phone: kpi.phone,
        manual_amount: parseFloat(kpi.kpi_value),
        note: kpi.note || '',
        sheet_row: kpi.sheet_row,
        synced: kpi.synced || false,
      })));
      
      // Сохраняем дату последней загрузки
      setLastLoadedDate(dateStr);
    } catch (error) {
      console.error('Ошибка загрузки KPI:', error);
      setKpiEntries([]);
    } finally {
      setLoading(false);
    }
  }, [selectedRestaurantId, lastLoadedDate]);


  // Загружаем KPI при открытии формы
  useEffect(() => {
    if (selectedRestaurantId && activeView === 'kpi') {
      fetchKPIEntries();
    }
  }, [selectedRestaurantId, activeView, fetchKPIEntries]);

  // Автоматическая проверка смены дня при фокусе на странице
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && selectedRestaurantId && activeView === 'kpi') {
        // Проверяем, сменился ли день
        const yesterdayDate = new Date();
        yesterdayDate.setDate(yesterdayDate.getDate() - 1);
        const dateStr = yesterdayDate.toISOString().split('T')[0];
        
        if (lastLoadedDate && lastLoadedDate !== dateStr) {
          console.log('Обнаружена смена дня, перезагружаем KPI');
          fetchKPIEntries();
        }
      }
    };

    // Проверка каждую минуту для надежного обнаружения смены дня
    const interval = setInterval(() => {
      if (selectedRestaurantId && activeView === 'kpi') {
        const yesterdayDate = new Date();
        yesterdayDate.setDate(yesterdayDate.getDate() - 1);
        const dateStr = yesterdayDate.toISOString().split('T')[0];
        
        if (lastLoadedDate && lastLoadedDate !== dateStr) {
          console.log('Обнаружена смена дня (по таймеру), перезагружаем KPI');
          fetchKPIEntries();
        }
      }
    }, 60000); // Проверяем каждую минуту

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [selectedRestaurantId, activeView, lastLoadedDate, fetchKPIEntries]);

  // Открыть модальное окно для добавления KPI
  const handleOpenKpiModal = () => {
    setEditingKpiId(null);
    setKpiFormData({ employee_name: '', manual_amount: '', note: '' });
    setShowEmployeeSuggestions(false);
    setIsKpiModalOpen(true);
  };

  // Открыть модальное окно для редактирования KPI
  const handleEditKpi = (kpi: KPIEntry) => {
    setEditingKpiId(kpi.kpi_id);
    setKpiFormData({
      employee_name: kpi.employee_name,
      manual_amount: kpi.manual_amount.toString(),
      note: kpi.note || '',
    });
    setIsKpiModalOpen(true);
  };

  // Удаление KPI
  const handleDeleteKpi = async (kpiId: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот KPI?')) {
      return;
    }

    try {
      await kpiService.deleteEmployeeKPI(kpiId);
      
      // Удаляем из локального состояния
      const updatedEntries = kpiEntries.filter((entry: KPIEntry) => entry.kpi_id !== kpiId);
      setKpiEntries(updatedEntries);
      
      // Если удаляли редактируемый элемент, сбрасываем режим редактирования
      if (editingKpiId === kpiId) {
        setEditingKpiId(null);
        setIsKpiModalOpen(false);
        setKpiFormData({ employee_name: '', manual_amount: '', note: '' });
        setShowEmployeeSuggestions(false);
      }
      
      success('KPI успешно удален!');
    } catch (error) {
      console.error('Ошибка удаления KPI:', error);
      showError(error instanceof Error ? error.message : 'Ошибка удаления KPI');
    }
  };

  // Сохранить KPI из модального окна
  const handleSaveKpiModal = async () => {
    if (!selectedRestaurantId) {
      showError('Выберите ресторан');
      return;
    }

    if (!kpiFormData.employee_name || !kpiFormData.employee_name.trim()) {
      showError('Введите имя сотрудника');
      return;
    }

    if (!kpiFormData.manual_amount || parseFloat(kpiFormData.manual_amount) <= 0) {
      showError('Введите сумму больше 0');
      return;
    }

    // Защита от повторных вызовов
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    const yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    
    if (editingKpiId) {
      // Показываем индикатор обновления
      setUpdatingKpiId(editingKpiId);
      
      try {
        // Обновление существующего KPI
        const result = await kpiService.updateEmployeeKPI(editingKpiId, {
          kpi_value: parseFloat(kpiFormData.manual_amount),
          note: kpiFormData.note,
        });
        
        // Обновляем локальное состояние
        setKpiEntries((prev: KPIEntry[]) => prev.map((entry: KPIEntry) => 
          entry.kpi_id === editingKpiId 
            ? {
                ...entry,
                manual_amount: parseFloat(kpiFormData.manual_amount),
                note: kpiFormData.note,
                synced: result.status === 'updated_and_synced',
              }
            : entry
        ));
        
        success('KPI успешно обновлен!');
      } catch (error) {
        console.error('Ошибка обновления KPI:', error);
        showError(error instanceof Error ? error.message : 'Ошибка обновления KPI');
      } finally {
        setUpdatingKpiId(null);
        setIsSaving(false);
        // Закрываем модальное окно после обновления
        setIsKpiModalOpen(false);
        setKpiFormData({ employee_name: '', manual_amount: '', note: '' });
        setEditingKpiId(null);
      }
    } else {
      // Создание нового KPI
      const tempId = `temp_${Date.now()}`;
      const employeeName = kpiFormData.employee_name;
      const manualAmount = parseFloat(kpiFormData.manual_amount);
      const noteText = kpiFormData.note;
      
      const tempEntry: KPIEntry = {
        kpi_id: tempId,
        employee_name: employeeName,
        position: '', // Будет получено от сервера
        phone: '', // Будет получено от сервера
        manual_amount: manualAmount,
        note: noteText,
        synced: false,
      };
      
      // Добавляем временную запись в список
      setKpiEntries((prev: KPIEntry[]) => [tempEntry, ...prev]);
      
      // Отправляем на сервер СНАЧАЛА (не закрываем модальное окно до отправки)
      try {
        const createPromise = kpiService.createEmployeeKPI({
          employee_name: employeeName,
          position: '', // Будет автозаполнено в бэкенде
          phone: '', // Будет автозаполнено в бэкенде
          restaurant_id: selectedRestaurantId,
          kpi_date: yesterdayDate.toISOString().split('T')[0],
          kpi_value: manualAmount,
          note: noteText,
        });
        
        // Закрываем модальное окно сразу после отправки
        setIsKpiModalOpen(false);
        setKpiFormData({ employee_name: '', manual_amount: '', note: '' });
        setEditingKpiId(null);
        setIsSaving(false);
        
        // Дожидаемся ответа в фоне
        const result = await createPromise;
        
        // Получаем полные данные KPI с сервера
        const fullKpi = await kpiService.getEmployeeKPI(result.kpi_id);
        
        // Заменяем временную запись на реальную
        setKpiEntries((prev: KPIEntry[]) => prev.map((entry: KPIEntry) => 
          entry.kpi_id === tempId 
            ? {
                kpi_id: fullKpi.kpi_id,
                employee_name: fullKpi.employee_name,
                position: fullKpi.position,
                phone: fullKpi.phone,
                manual_amount: parseFloat(fullKpi.kpi_value.toString()),
                note: fullKpi.note || '',
                sheet_row: fullKpi.sheet_row,
                synced: fullKpi.synced || false,
              }
            : entry
        ));
        
        success('KPI успешно начислен!');
      } catch (error) {
        console.error('Ошибка создания KPI:', error);
        showError(error instanceof Error ? error.message : 'Ошибка создания KPI');
        // Удаляем временную запись при ошибке
        setKpiEntries((prev: KPIEntry[]) => prev.filter((entry: KPIEntry) => entry.kpi_id !== tempId));
      }
    }
  };

  const handleCreateRestaurant = async (data: { name: string; googleSheetUrl: string }) => {
    try {
      const response = await fetch('/api/restaurants/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: data.name,
          google_sheet_url: data.googleSheetUrl,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка создания ресторана');
      }

      await fetchRestaurantsHook();
      setIsRestaurantFormOpen(false);
      success('Ресторан создан успешно!');
    } catch (error) {
      console.error('Ошибка создания ресторана:', error);
      showError(error instanceof Error ? error.message : 'Ошибка создания ресторана');
    }
  };
  
  
  // Фильтрация сотрудников для табеля часов
  useEffect(() => {
    const currentInput = editingTimesheetRow ? timesheetEditData.name : newTimesheetEntry.name;
    
    if (showTimesheetSuggestions) {
      console.log('[TIMESHEET] employeeSuggestions sample:', employeeSuggestions[0]);
      console.log('[TIMESHEET] Имя первого сотрудника (с кодами символов):', employeeSuggestions[0]?.name?.split('').map((c: string, i: number) => `[${i}]='${c}'(${c.charCodeAt(0)})`).join(' '));
      if (currentInput.trim()) {
        // Фильтруем если что-то введено (ищем по имени И должности)
        const filtered = employeeSuggestions.filter((emp: any) =>
          emp.name?.toLowerCase().includes(currentInput.toLowerCase()) ||
          emp.position?.toLowerCase().includes(currentInput.toLowerCase())
        );
        console.log(`[TIMESHEET] Filtered ${filtered.length} из ${employeeSuggestions.length} для "${currentInput}"`);
        setFilteredTimesheetEmployees(filtered);
      } else {
        // Показываем все имена если поле пустое
        setFilteredTimesheetEmployees(employeeSuggestions);
      }
    }
  }, [timesheetEditData.name, newTimesheetEntry.name, employeeSuggestions, editingTimesheetRow, showTimesheetSuggestions]);
  
  // Загрузка табеля часов
  const fetchTimesheetData = useCallback(async () => {
    if (!selectedRestaurantId || activeView !== 'shifts') return;
    
    setTimesheetLoading(true);
    try {
      const entries = await timesheetService.getTimesheetHours(selectedRestaurantId);
      setTimesheetEntries(entries);
    } catch (error: any) {
      console.error('Ошибка загрузки табеля часов:', error);
      showError(error.message || 'Ошибка загрузки табеля часов');
    } finally {
      setTimesheetLoading(false);
    }
  }, [selectedRestaurantId, activeView, showError]);
  
  useEffect(() => {
    if (activeView === 'shifts' && selectedRestaurantId) {
      fetchTimesheetData();
    }
  }, [activeView, selectedRestaurantId, fetchTimesheetData]);
  
  // Добавление записи в табель
  const handleAddTimesheetEntry = async () => {
    if (!selectedRestaurantId) {
      showError('Выберите ресторан');
      return;
    }
    
    if (!newTimesheetEntry.name || !newTimesheetEntry.name.trim()) {
      showError('Введите имя сотрудника');
      return;
    }
    
    // Часы необязательны
    const hours = newTimesheetEntry.hours.trim() || '';
    
    const manualAmount = Math.round(parseFloat(newTimesheetEntry.manual_amount || '0'));
    if (isNaN(manualAmount) || manualAmount < 0) {
      showError('Введите корректное начисление вручную');
      return;
    }
    
    // Проверяем, нет ли уже такого сотрудника в списке (точное совпадение)
    const isDuplicate = timesheetEntries.some((entry: TimesheetEntry) => 
      entry.name === newTimesheetEntry.name
    );
    
    if (isDuplicate) {
      showError('Сотрудник уже добавлен');
      return;
    }
    
    console.log('[FRONTEND] Отправка данных:', { name: newTimesheetEntry.name, hours, manualAmount });
    
    // Создаем временный ID и временную запись
    const tempSheetRow = -Date.now(); // Отрицательное число для временной записи
    const tempEntry: TimesheetEntry = {
      name: newTimesheetEntry.name,
      position: '', // Пусто, пока не придет с сервера
      hours: hours || '0',
      manual_amount: manualAmount,
      sheet_row: tempSheetRow,
      start_time: '',
      end_time: '',
      syncing: true // Флаг синхронизации
    };
    
    // Добавляем временную запись в список
    setTimesheetEntries((prev: TimesheetEntry[]) => [...prev, tempEntry]);
    
    // Отправляем на сервер (не дожидаемся ответа)
    setTimesheetSaving(true);
    const addPromise = timesheetService.addTimesheetEntry(
      selectedRestaurantId,
      newTimesheetEntry.name,
      hours || '0',
      manualAmount
    );
    
    // Закрываем форму сразу после отправки
    setIsAddTimesheetOpen(false);
    setNewTimesheetEntry({ name: '', hours: '', manual_amount: '' });
    setTimesheetSaving(false);
    
    // Дожидаемся ответа в фоне
    try {
      const response = await addPromise;
      
      // Заменяем временную запись на реальную
      setTimesheetEntries((prev: TimesheetEntry[]) => prev.map(entry => 
        entry.sheet_row === tempSheetRow
          ? {
              name: response.name,
              position: response.position,
              hours: typeof response.hours === 'string' ? response.hours : response.hours.toString(),
              manual_amount: response.manual_amount,
              sheet_row: response.sheet_row,
              start_time: response.start_time || '',
              end_time: response.end_time || '',
              syncing: false // Синхронизация завершена
            }
          : entry
      ));
      
      success('Запись добавлена!');
    } catch (error: any) {
      console.error('Ошибка добавления записи:', error);
      
      // Удаляем временную запись при ошибке
      setTimesheetEntries((prev: TimesheetEntry[]) => prev.filter(entry => entry.sheet_row !== tempSheetRow));
      
      showError(error.message || 'Ошибка добавления записи');
    }
  };
  
  // Начать редактирование записи
  const handleStartEditTimesheet = (entry: TimesheetEntry) => {
    setEditingTimesheetRow(entry.sheet_row);
    setTimesheetEditData({
      name: entry.name,
      hours: entry.hours.toString(),
      start_time: entry.start_time || '',
      end_time: entry.end_time || '',
      manual_amount: entry.manual_amount.toString()
    });
  };
  
  // Сохранить изменения записи
  const handleSaveTimesheetEdit = async (sheetRow: number) => {
    if (!selectedRestaurantId) return;
    
    if (!timesheetEditData.name || !timesheetEditData.name.trim()) {
      showError('Введите имя сотрудника');
      return;
    }
    
    // Часы необязательны, но если введены - должны быть в формате HH:MM
    const hours = timesheetEditData.hours.trim();
    
    // Если часы введены, проверяем формат
    if (hours && !hours.match(/^\d+:\d{2}$/)) {
      showError('Неверный формат часов. Используйте формат HH:MM (например, 8:30)');
      return;
    }
    
    const manualAmount = Math.round(parseFloat(timesheetEditData.manual_amount || '0'));
    if (isNaN(manualAmount) || manualAmount < 0) {
      showError('Введите корректное начисление вручную');
      return;
    }
    
    setTimesheetSaving(true);
    try {
      await timesheetService.updateTimesheetEntry(
        selectedRestaurantId,
        sheetRow,
        timesheetEditData.name,
        hours, // Передаем строку в формате HH:MM
        manualAmount
      );
      
      // Обновляем локальный стейт
      setTimesheetEntries((prev: TimesheetEntry[]) => prev.map((entry: TimesheetEntry) =>
        entry.sheet_row === sheetRow
          ? { ...entry, hours, manual_amount: manualAmount }
          : entry
      ));
      
      setEditingTimesheetRow(null);
      setTimesheetEditData({ name: '', hours: '', start_time: '', end_time: '', manual_amount: '' });
      success('Запись обновлена!');
    } catch (error: any) {
      console.error('Ошибка обновления записи:', error);
      showError(error.message || 'Ошибка обновления записи');
    } finally {
      setTimesheetSaving(false);
    }
  };
  
  // Отменить редактирование
  const handleCancelTimesheetEdit = () => {
    setEditingTimesheetRow(null);
    setTimesheetEditData({ name: '', hours: '', start_time: '', end_time: '', manual_amount: '' });
  };

  // Удаление записи из табеля
  const handleDeleteTimesheetEntry = async (sheetRow: number) => {
    if (!confirm('Вы уверены, что хотите удалить эту запись?')) {
      return;
    }

    if (!selectedRestaurantId) {
      showError('Выберите ресторан');
      return;
    }

    try {
      await timesheetService.deleteTimesheetEntry(selectedRestaurantId, sheetRow);
      
      // Удаляем из локального состояния
      setTimesheetEntries((prev: TimesheetEntry[]) => prev.filter((entry: TimesheetEntry) => entry.sheet_row !== sheetRow));
      
      success('Запись успешно удалена!');
    } catch (error: any) {
      console.error('Ошибка удаления записи:', error);
      showError(error.message || 'Ошибка удаления записи');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <Header 
        restaurants={restaurants}
        selectedRestaurantId={selectedRestaurantId}
        onSelectRestaurant={setSelectedRestaurantId}
        onAddRestaurantClick={() => setIsRestaurantFormOpen(true)}
        isAdmin={isAdmin}
      />
      
      {/* Toast notifications */}
      {toasts.map((toast: { id: string; message: string; type: ToastType }) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => hideToast(toast.id)}
        />
      ))}

      <div className="container mx-auto px-4 py-8">
        {/* Главное меню с 2 кнопками */}
        {selectedRestaurantId && activeView === 'menu' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {/* Кнопка: Начислить KPI за вчера */}
            <button
              onClick={() => setActiveView('kpi')}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg p-5 shadow-md transition-all hover:shadow-lg flex flex-col items-center justify-center gap-2"
            >
              <ChartBarIcon className="w-10 h-10" />
              <span className="text-base font-semibold" style={{fontFamily: "'Times New Roman', Times, serif"}}>Начислить KPI за вчера</span>
            </button>

            {/* Кнопка: Отредактировать часы */}
            <button
              onClick={() => setActiveView('shifts')}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg p-5 shadow-md transition-all hover:shadow-lg flex flex-col items-center justify-center gap-2"
            >
              <ClockIcon className="w-10 h-10" />
              <span className="text-base font-semibold" style={{fontFamily: "'Times New Roman', Times, serif"}}>Отредактировать часы</span>
            </button>
          </div>
        )}

        {/* Форма начисления KPI */}
        {selectedRestaurantId && activeView === 'kpi' && (
          <>
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{fontFamily: "'Times New Roman', Times, serif"}}>
            Начисление KPI за вчера
          </h1>
          <p className="text-base text-gray-600" style={{fontFamily: "'Times New Roman', Times, serif"}}>
            Дата: <span className="font-semibold">{yesterdayStr}</span>
          </p>
        </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Кнопки управления */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <Button
                onClick={handleOpenKpiModal}
                className="bg-green-600 hover:bg-green-700 text-white flex items-center justify-center py-2.5 rounded-lg transition-all shadow-sm hover:shadow-md text-sm"
              >
                <PlusIcon className="w-4 h-4 mr-1.5" />
                <span style={{fontFamily: "'Times New Roman', Times, serif"}}>Начислить KPI</span>
              </Button>
              <Button
                onClick={() => setActiveView('menu')}
                variant="secondary"
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center py-2.5 rounded-lg transition-all shadow-sm hover:shadow-md border border-gray-300 text-sm"
              >
                <span style={{fontFamily: "'Times New Roman', Times, serif"}}>Назад</span>
              </Button>
            </div>

            {/* Список начисленных KPI */}
            {loading ? (
              <div className="text-center py-8 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2">Загрузка...</p>
              </div>
            ) : kpiEntries.length > 0 ? (
              <div className="space-y-4">
                {kpiEntries.map((entry: KPIEntry) => {
                  const needsSync = !entry.synced;
                  const isUpdating = updatingKpiId === entry.kpi_id;
                  
                  return (
                  <div
                    key={entry.kpi_id}
                    className={`bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all ${isUpdating ? 'bg-yellow-50' : ''}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-700 font-semibold text-sm" style={{fontFamily: "'Times New Roman', Times, serif"}}>
                              {entry.employee_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="text-base font-semibold text-gray-900 line-clamp-2" style={{fontFamily: "'Times New Roman', Times, serif", whiteSpace: 'pre-wrap'}}>
                                {entry.employee_name}
                              </h3>
                              {isUpdating && (
                                <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full animate-pulse">
                                  Обновление...
                                </span>
                              )}
                              {needsSync && !isUpdating && (
                                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full animate-pulse">
                                  Синхронизируем...
                                </span>
                              )}
                              {entry.synced && !isUpdating && (
                                <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                                  Синхронизирован
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600" style={{fontFamily: "'Times New Roman', Times, serif"}}>
                              {entry.position || 'Должность не указана'}
                            </p>
                            {entry.sheet_row && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                Строка: {entry.sheet_row}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-3 border border-gray-200">
                          <p className="text-xl font-bold text-gray-800" style={{fontFamily: "'Times New Roman', Times, serif"}}>
                            {entry.manual_amount.toFixed(2)} ₽
                          </p>
                          {entry.note && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="text-sm text-gray-700 italic leading-relaxed line-clamp-2" style={{fontFamily: "'Times New Roman', Times, serif"}}>
                                {entry.note}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex-shrink-0 pt-1 flex gap-1">
                        <button
                          onClick={() => handleEditKpi(entry)}
                          disabled={isUpdating}
                          className={`p-2 rounded-lg transition-colors ${
                            isUpdating
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-blue-600 hover:bg-blue-50'
                          }`}
                          title="Редактировать"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteKpi(entry.kpi_id)}
                          disabled={isUpdating}
                          className={`p-2 rounded-lg transition-colors ${
                            isUpdating
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                          title="Удалить"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
                  </div>
                ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">Нет начислений за вчера</p>
                <p className="text-sm mt-2">Нажмите "Добавить KPI" чтобы начислить</p>
                  </div>
                )}
          </div>
          </>
        )}

        {/* Редактирование табеля часов */}
        {selectedRestaurantId && activeView === 'shifts' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" style={{fontFamily: "'Times New Roman', Times, serif"}}>Табель часов</h2>
            
            {/* Кнопки управления */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <Button
                onClick={() => setIsAddTimesheetOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white flex items-center justify-center py-2.5 rounded-lg transition-all shadow-sm hover:shadow-md text-sm"
              >
                <PlusIcon className="w-4 h-4 mr-1.5" />
                <span style={{fontFamily: "'Times New Roman', Times, serif"}}>Добавить сотрудника</span>
              </Button>
              <Button
                onClick={() => setActiveView('menu')}
                variant="secondary"
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center py-2.5 rounded-lg transition-all shadow-sm hover:shadow-md border border-gray-300 text-sm"
              >
                <span style={{fontFamily: "'Times New Roman', Times, serif"}}>Назад</span>
              </Button>
            </div>
            
            {/* Поле поиска сотрудника */}
            {!timesheetLoading && timesheetEntries.length > 0 && (
              <div className="mb-4 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={timesheetSearchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTimesheetSearchQuery(e.target.value)}
                  placeholder="Найти сотрудника..."
                  className="w-full pl-10 pr-4 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{fontFamily: "'Times New Roman', Times, serif"}}
                />
                {timesheetSearchQuery && (
                  <button
                    onClick={() => setTimesheetSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
            
            {timesheetLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Загрузка...</p>
              </div>
            ) : timesheetEntries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-lg">Нет записей в табеле</p>
                <p className="text-sm mt-2">Нажмите "Добавить сотрудника" чтобы начать</p>
              </div>
            ) : (
              (() => {
                const filteredEntries = timesheetEntries.filter((entry: TimesheetEntry) => 
                  entry.name.toLowerCase().includes(timesheetSearchQuery.toLowerCase()) ||
                  entry.position?.toLowerCase().includes(timesheetSearchQuery.toLowerCase())
                );
                
                if (filteredEntries.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-lg">Никого не найдено</p>
                      <p className="text-sm mt-2">Попробуйте изменить запрос</p>
                    </div>
                  );
                }
                
                return (
              <div className="space-y-4">
                {filteredEntries.map((entry: TimesheetEntry) => {
                  const isSyncing = entry.syncing === true;
                  
                  return (
                  <div 
                    key={entry.sheet_row} 
                    className={`bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all ${isSyncing ? 'bg-blue-50' : ''}`}
                  >
                    {editingTimesheetRow === entry.sheet_row ? (
                      // Режим редактирования
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Сотрудник
                          </label>
                          <div className="relative" ref={timesheetInputRef}>
                            <input
                              type="text"
                              value={timesheetEditData.name}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTimesheetEditData({ ...timesheetEditData, name: e.target.value })}
                              onFocus={() => setShowTimesheetSuggestions(true)}
                              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                              placeholder="Введите имя"
                            />
                            
                              {showTimesheetSuggestions && (
                              <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
                                {filteredTimesheetEmployees.length > 0 ? (
                                  filteredTimesheetEmployees.map((employee: any, index: number) => (
                                    <button
                                      key={index}
                                      type="button"
                                      className="w-full px-4 py-3 text-left hover:bg-blue-100 focus:bg-blue-100 focus:outline-none transition-colors"
                                      onMouseDown={(e: React.MouseEvent) => {
                                        e.preventDefault();
                                        setTimesheetEditData({ ...timesheetEditData, name: employee.name });
                                        setShowTimesheetSuggestions(false);
                                      }}
                                    >
                                      <div className="font-medium" style={{whiteSpace: 'pre-wrap'}}>{highlightMatch(employee.name, timesheetEditData.name)}</div>
                                      {employee.position && (
                                        <div className="text-sm text-gray-500">{employee.position}</div>
                                      )}
                                    </button>
                                  ))
                                ) : (
                                  <div className="px-4 py-3 text-gray-500 text-sm">
                                    Никого не найдено
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {(timesheetEditData.start_time || timesheetEditData.end_time) && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Начало смены
                              </label>
                              <input
                                type="text"
                                value={timesheetEditData.start_time}
                                readOnly
                                disabled
                                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Конец смены
                              </label>
                              <input
                                type="text"
                                value={timesheetEditData.end_time}
                                readOnly
                                disabled
                                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                              />
                            </div>
                          </div>
                        )}
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Итого (часы)
                          </label>
                          <input
                            type="text"
                            value={timesheetEditData.hours}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTimesheetEditData({ ...timesheetEditData, hours: e.target.value })}
                            className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            placeholder="8:30"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Начисление вручную (₽)
                          </label>
                          <input
                            type="number"
                            inputMode="numeric"
                            value={timesheetEditData.manual_amount}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTimesheetEditData({ ...timesheetEditData, manual_amount: e.target.value })}
                            className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            placeholder="Введите сумму"
                            step="0.01"
                            min="0"
                          />
                        </div>
                        
                        <div className="flex gap-3 pt-2">
                          <Button 
                            onClick={() => handleSaveTimesheetEdit(entry.sheet_row)} 
                            className="flex-1"
                            disabled={timesheetSaving}
                          >
                            {timesheetSaving ? 'Сохранение...' : 'Сохранить'}
                          </Button>
                          <Button 
                            onClick={handleCancelTimesheetEdit} 
                            variant="secondary" 
                            className="flex-1"
                            disabled={timesheetSaving}
                          >
                            Отмена
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // Режим просмотра
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-indigo-700 font-semibold text-sm" style={{fontFamily: "'Times New Roman', Times, serif"}}>
                                {entry.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base font-semibold text-gray-900 line-clamp-2" style={{fontFamily: "'Times New Roman', Times, serif", whiteSpace: 'pre-wrap'}}>
                                {entry.name}
                              </h3>
                              {isSyncing ? (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                  <p className="text-sm text-blue-600">Синхронизация...</p>
                                </div>
                              ) : entry.position ? (
                                <p className="text-sm text-gray-600 mt-0.5" style={{fontFamily: "'Times New Roman', Times, serif"}}>
                                  {entry.position}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-3 border border-blue-200 flex-1">
                              <p className="text-xs text-gray-500 mb-1">Время</p>
                              <p className="text-lg font-bold text-blue-700" style={{fontFamily: "'Times New Roman', Times, serif"}}>
                                {entry.start_time || '--'} - {entry.end_time || '--'}
                              </p>
                            </div>
                            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-3 border border-gray-200 flex-1">
                              <p className="text-xs text-gray-500 mb-1">Часы</p>
                              <p className="text-lg font-bold text-gray-800" style={{fontFamily: "'Times New Roman', Times, serif"}}>
                                {entry.hours || '--'}
                              </p>
                            </div>
                            {entry.manual_amount > 0 && (
                              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200 flex-1">
                                <p className="text-xs text-gray-500 mb-1">Начисление</p>
                                <p className="text-lg font-bold text-green-700" style={{fontFamily: "'Times New Roman', Times, serif"}}>
                                  {formatMoney(entry.manual_amount)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex-shrink-0 pt-1 flex gap-1">
                          <button
                            onClick={() => handleStartEditTimesheet(entry)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Редактировать"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteTimesheetEntry(entry.sheet_row)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Удалить"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
                );
              })()
            )}
          </div>
        )}

        {!selectedRestaurantId && (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-gray-500">
            Выберите ресторан для начисления KPI
          </div>
        )}
      </div>

      <RestaurantForm
        isOpen={isRestaurantFormOpen}
        onClose={() => setIsRestaurantFormOpen(false)}
        onSubmit={handleCreateRestaurant}
      />
      
      
      {/* Модальное окно для добавления/редактирования KPI */}
      <Modal
        isOpen={isKpiModalOpen}
        onClose={() => {
          setIsKpiModalOpen(false);
          setKpiFormData({ employee_name: '', manual_amount: '', note: '' });
          setEditingKpiId(null);
          setShowEmployeeSuggestions(false);
        }}
        title={editingKpiId ? "Редактировать KPI" : "Добавить KPI"}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Имя сотрудника <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={employeeInputRef}>
              <input
                type="text"
                value={kpiFormData.employee_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  setKpiFormData({ ...kpiFormData, employee_name: e.target.value })
                }
                onFocus={() => {
                  if (!editingKpiId) {
                    setShowEmployeeSuggestions(true);
                  }
                }}
                placeholder="Выберите или введите имя"
                disabled={!!editingKpiId}
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              
              {showEmployeeSuggestions && !editingKpiId && (
                <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
                  {filteredEmployees.length > 0 ? (
                    filteredEmployees.map((employee: any, index: number) => (
                      <button
                        key={index}
                        type="button"
                        className="w-full px-4 py-3 text-left hover:bg-blue-100 focus:bg-blue-100 focus:outline-none transition-colors"
                        onMouseDown={(e: React.MouseEvent) => {
                          e.preventDefault();
                          setKpiFormData({ ...kpiFormData, employee_name: employee.name });
                          setShowEmployeeSuggestions(false);
                        }}
                      >
                        <div className="font-medium" style={{whiteSpace: 'pre-wrap'}}>{highlightMatch(employee.name, kpiFormData.employee_name)}</div>
                        {employee.position && (
                          <div className="text-sm text-gray-500">{employee.position}</div>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-500 text-sm">
                      Никого не найдено
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Сумма (₽) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              inputMode="decimal"
              value={kpiFormData.manual_amount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setKpiFormData({ ...kpiFormData, manual_amount: e.target.value })
              }
              placeholder="0"
              step="0.01"
              min="0"
              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Комментарий
            </label>
            <textarea
              value={kpiFormData.note}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                setKpiFormData({ ...kpiFormData, note: e.target.value })
              }
              placeholder="Необязательно"
              rows={3}
              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSaveKpiModal}
              disabled={isSaving}
              className="flex-1 text-lg py-3"
            >
              {isSaving ? 'Сохранение...' : (editingKpiId ? 'Сохранить' : 'Добавить')}
            </Button>
            <Button
              onClick={() => {
                setIsKpiModalOpen(false);
                setKpiFormData({ employee_name: '', manual_amount: '', note: '' });
                setEditingKpiId(null);
                setShowEmployeeSuggestions(false);
              }}
              variant="secondary"
              className="flex-1 text-lg py-3"
            >
              Отмена
            </Button>
          </div>
        </div>
      </Modal>

      {/* Модальное окно для добавления записи в табель */}
      <Modal 
        isOpen={isAddTimesheetOpen} 
        onClose={() => {
          setIsAddTimesheetOpen(false);
          setNewTimesheetEntry({ name: '', hours: '', manual_amount: '' });
          setShowTimesheetSuggestions(false);
        }} 
        title="Добавить запись в табель"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Сотрудник <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={timesheetInputRef}>
              <input
                type="text"
                value={newTimesheetEntry.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  setNewTimesheetEntry({ ...newTimesheetEntry, name: e.target.value })
                }
                onFocus={() => setShowTimesheetSuggestions(true)}
                placeholder="Выберите или введите имя"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              
              {showTimesheetSuggestions && (
                <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
                  {filteredTimesheetEmployees.length > 0 ? (
                    filteredTimesheetEmployees.map((employee: any, index: number) => (
                      <button
                        key={index}
                        type="button"
                        className="w-full px-4 py-3 text-left hover:bg-blue-100 focus:bg-blue-100 focus:outline-none transition-colors"
                        onMouseDown={(e: React.MouseEvent) => {
                          e.preventDefault();
                          setNewTimesheetEntry({ ...newTimesheetEntry, name: employee.name });
                          setShowTimesheetSuggestions(false);
                        }}
                      >
                        <div className="font-medium" style={{whiteSpace: 'pre-wrap'}}>{highlightMatch(employee.name, newTimesheetEntry.name)}</div>
                        {employee.position && (
                          <div className="text-sm text-gray-500">{employee.position}</div>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-500 text-sm">
                      Никого не найдено
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Количество часов <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              inputMode="numeric"
              value={newTimesheetEntry.hours}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setNewTimesheetEntry({ ...newTimesheetEntry, hours: e.target.value })
              }
              placeholder="0"
              step="1"
              min="0"
              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Начисление вручную (₽)
            </label>
            <input
              type="number"
              inputMode="decimal"
              value={newTimesheetEntry.manual_amount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setNewTimesheetEntry({ ...newTimesheetEntry, manual_amount: e.target.value })
              }
              placeholder="Введите сумму"
              step="0.01"
              min="0"
              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button 
              onClick={handleAddTimesheetEntry} 
              className="flex-1 text-lg py-3 flex items-center justify-center"
              disabled={timesheetSaving}
            >
              {timesheetSaving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Добавление...
                </>
              ) : (
                'Добавить'
              )}
            </Button>
            <Button 
              onClick={() => {
                setIsAddTimesheetOpen(false);
                setNewTimesheetEntry({ name: '', hours: '', manual_amount: '' });
                setShowTimesheetSuggestions(false);
              }} 
              variant="secondary" 
              className="flex-1 text-lg py-3"
              disabled={timesheetSaving}
            >
              Отмена
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default KPIPage;
